import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import classNames from "classnames/bind";

import { type RootState } from "../../../Application/globaltypes/redux";
import * as reportingActions from "../../state/packs/packActions";
import { selectOwnerPacksLicensingState } from "../../state/packs/packSlice";

import SpPackReportList from "./SpPackReportList";
import SpPackReportDrilldown from "./drilldown/SpPackReportDrilldown";
import { RequestStatusRenderer } from "../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { OverviewHeader, ContentView } from "components";
import cn from "classnames";

import styles from "./spPackReport.module.scss";
import { dateRangeAllTime, defaultDateFilter } from "features/Library/Common/utils/performanceUtils";
import { type AccountType } from "interfaces/Account";
import { useReportingAccountType } from "components/reportingFilter/ReportingFilterRequests";

const cx = classNames.bind(styles);

export type SpPackReportFilter = {
  isPaid: boolean;
  type: AccountType | undefined;
  accounts?: string[];
  includeMyData?: boolean;
  dateFrom: string;
  dateTo: string;
};

export const SpPackReport: FC<PropsFromRedux> = ({ ownerPacksLicensingState, actions, accountId }) => {
  const [selectedPack, setSelectedPack] = useState<{ id: number; name: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const type = useReportingAccountType(accountId, true);
  const isPaid = activeTab === 0;

  const filter: SpPackReportFilter = useMemo(() => {
    const filter = defaultDateFilter({
      initialDateRange: dateRangeAllTime(),
      includeAccountsDropdown: true,
      accountId,
    });

    const filterWithIsPaid = { isPaid, type, ...filter };

    return filterWithIsPaid;
  }, [isPaid, type, accountId]);

  useEffect(() => {
    actions.fetchOwnerPacksLicensing(filter);
  }, [actions, filter]);

  const handlePackSelectionReset = useCallback(() => setSelectedPack(undefined), []);

  const renderRoot = useMemo(
    () => (
      <div className={styles.table}>
        <RequestStatusRenderer state={ownerPacksLicensingState}>
          {
            <SpPackReportList
              isPaid={isPaid}
              reduxFinished
              ownerPackLicenseData={ownerPacksLicensingState.value}
              onPackSelection={setSelectedPack}
            />
          }
        </RequestStatusRenderer>
      </div>
    ),
    [ownerPacksLicensingState, isPaid],
  );

  const drilldownElement = selectedPack && (
    <SpPackReportDrilldown
      filter={filter}
      packId={selectedPack.id}
      packName={selectedPack.name}
      resetSelectedPack={handlePackSelectionReset}
    />
  );

  const component = drilldownElement ?? renderRoot;

  const tabs = useMemo(
    () => [
      {
        label: "Paid",
        component,
      },
      {
        label: "Free",
        component,
      },
    ],
    [component],
  );

  return (
    <div className={cx(styles["full-height"])}>
      <div className={styles.body}>
        <OverviewHeader title="Packs & Licensing" style={{ minHeight: "auto" }} />
        <div className={styles.scrollableBody}>
          <div className={cn(styles.SpReportTabs)}>
            <ContentView
              activeIndex={activeTab}
              onTabChange={(_e, { activeIndex }) => {
                setSelectedPack(undefined);
                setActiveTab(activeIndex);
              }}
              tabs={tabs}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  ownerPacksLicensingState: selectOwnerPacksLicensingState(state),
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(reportingActions, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SpPackReport);
export default ConnectedComponent;
